// Tipos da arte do Campus Agzos. Tudo aqui é DADO puro (sem DOM): os testes validam
// em Node e o navegador "assa" em canvas (src/web/engine/bake.ts).

import type { Dir, SpotKind, SpotUse } from '../../core/world.js';

/**
 * Um sprite: `rows` tem `h` strings de `w` caracteres. '.' é transparente; qualquer
 * outro caractere precisa estar em `palette`, que leva caractere → TOKEN da paleta
 * (ex.: { o: 'wood0', W: 'wood2' }). Nunca hex solto.
 */
export interface PixelSprite {
  w: number;
  h: number;
  rows: readonly string[];
  palette: Readonly<Record<string, string>>;
}

export type FurnitureCategory =
  | 'office'
  | 'reception'
  | 'kitchen'
  | 'lounge'
  | 'restroom'
  | 'meeting'
  | 'servers'
  | 'decor'
  | 'wall'
  | 'exterior';

/** Um ponto de uso que o móvel oferece, relativo ao tile superior esquerdo da pegada. */
export interface FurnitureSpot {
  dx: number;
  dy: number;
  facing: Dir;
  kind: SpotKind;
  use: SpotUse;
}

/**
 * Um móvel.
 *
 * Âncora: o sprite é desenhado com a BASE alinhada à base da pegada e centralizado na
 * horizontal. Ele pode ser mais alto que a pegada (é a altura em 3/4: uma estante de
 * 1×1 tile tem sprite 16×32). Largura do sprite ≤ footprint.w × 16.
 */
export interface FurnitureDef {
  id: string;
  /** Nome em português (aparece no preview). */
  name: string;
  category: FurnitureCategory;
  /** Pegada no chão, em tiles: o que bloqueia a caminhada. */
  footprint: { w: number; h: number };
  sprite: PixelSprite;
  /** Quadros extras de animação (mesmo w/h do sprite), trocados a cada `frameMs`. */
  frames?: readonly PixelSprite[];
  frameMs?: number;
  /** Vai na parede norte (relógio, janela, quadro). Não bloqueia. */
  wall?: boolean;
  /** Bloqueia a caminhada? Padrão: true (tapete e itens de parede: false). */
  blocks?: boolean;
  /** Ajuste fino da ordem de desenho, em px (negativo = desenha antes; cadeira: -4). */
  sortBias?: number;
  /** Pontos de uso (cadeira da mesa, sofá, pia…). */
  spots?: readonly FurnitureSpot[];
  /** Brilho à noite (janela, abajur, poste): cor CSS e raio em px. */
  glow?: { color: string; radius: number; dx?: number; dy?: number };
}

/** Um tile de piso (16×16), com variações opcionais escolhidas por hash da posição. */
export interface TileDef {
  id: string;
  name: string;
  sprite: PixelSprite;
  variants?: readonly PixelSprite[];
}

// ---------------------------------------------------------------- personagens

/** Direções desenhadas. 'right' é 'left' espelhado. */
export interface DirSet<T> {
  down: T;
  up: T;
  left: T;
}

export type ArmPose = 'down' | 'type' | 'raise';
export type LegPose = 'stand' | 'stepA' | 'stepB' | 'sit';

/**
 * Geometria do personagem (quadro de 16×24, pés na linha 23):
 *   cabeça  16×12 em y=0
 *   tronco  16×8  em y=11
 *   pernas  16×6  em y=18
 * Sentado, cabeça e tronco descem SIT_DY px.
 */
export const CHAR_W = 16;
export const CHAR_H = 24;
export const HEAD = { y: 0, h: 12 } as const;
export const TORSO = { y: 11, h: 8 } as const;
export const LEGS = { y: 18, h: 6 } as const;
export const SIT_DY = 2;
/** Colunas do tronco que a roupa pode pintar (os braços ficam fora: 0–3 e 12–15). */
export const OUTFIT_COLS = { from: 4, to: 11 } as const;

/** Um corpo: cabeça, tronco (por pose de braço) e pernas (por pose de perna). Slots: skin, shirt, pants, shoe. */
export interface BodyDef {
  id: string;
  name: string;
  head: DirSet<PixelSprite>;
  torso: DirSet<Record<ArmPose, PixelSprite>>;
  legs: DirSet<Record<LegPose, PixelSprite>>;
}

/** Onde uma sobreposição se ancora (define o tamanho do quadro). */
export type OverlayAnchor = 'head' | 'torso' | 'legs';

/**
 * Uma sobreposição: cabelo, roupa ou acessório. `frames` vai por cima da parte
 * ancorada, com o tamanho da âncora (cabeça 16×12, tronco 16×8, pernas 16×6).
 * `behind` (opcional) é um quadro inteiro 16×24 desenhado ATRÁS do corpo — ex.: cabelo
 * comprido que aparece atrás dos ombros.
 */
export interface OverlayDef {
  id: string;
  name: string;
  anchor: OverlayAnchor;
  frames: DirSet<PixelSprite>;
  behind?: Partial<DirSet<PixelSprite>>;
  /** Roupa: sobreposição extra nas pernas (saia, macacão), 16×6. */
  legs?: DirSet<PixelSprite>;
}
